/**
 * The intelligence report: what is going to break, what it will cost, whose
 * responsibility it is — and how far each of those can be defended.
 *
 * Three columns, and the third is the point. A prediction on its own is an
 * opinion; a prediction with a cost is a budget request; a prediction with a
 * cost and a named party is a dispute. What a chief executive can put in
 * front of a board or a regulator is the fourth thing: each claim tied to the
 * record it rests on, with the weaknesses stated before somebody else finds
 * them. So every finding here carries its evidence, and every finding is
 * graded — strong, partial or weak — with the reasons written out.
 *
 * Nothing in this module is invented. Each finding is derived from records
 * the platform already holds: predictive and reactive alerts with the rule
 * that raised them, work orders with their crews and due dates, the deferred
 * cost register, the mandatory tests and their intervals, compliance
 * obligations, the reliability fits, the model drift checks, the records
 * ledger and the project hold points. Where a record does not support a
 * claim — a cost the platform cannot price, a party it cannot name, a
 * threshold nobody has agreed — the report says so in that finding rather
 * than leaving the column blank or filling it in.
 *
 * Pure: the same inputs give the same report. The page gathers the inputs.
 */
import type { Alert, AlertExplanation } from "./alerts";
import type { ComplianceReport, Rule } from "./compliance";
import type { DriftCheck } from "./drift";
import type { Divergence, FactSummary } from "./ledger";
import type { LiabilityRegister, LiabilityRow } from "./liability";
import type { HoldPointReport } from "./projects";
import type { Fit, Population } from "./reliability";
import type { TestingSummary } from "./testing";
import { isOpen, type WorkOrder } from "./workOrders";
import { t, tn } from "../i18n";

export type EvidenceKind =
  "alert" | "rule" | "work_order" | "test" | "obligation" | "fit" | "drift" | "liability" | "ledger" | "hold_point";

/** A record the report rests on. `ref` is what a reader would look up. */
export interface Evidence {
  kind: EvidenceKind;
  ref: string;
  label: string;
  detail?: string;
  href?: string;
}

export type Grade = "strong" | "partial" | "weak";

export interface Finding {
  id: string;
  headline: string;
  /** where on the estate, in the platform's own terms */
  where?: string;
  /** 1 is worst, as the alerts and work orders have it */
  severity: number;
  /** "will break" or "has broken and is still open" */
  phase: "ahead" | "now";
  horizon: { text: string; at?: string; days?: number };
  prediction: { statement: string; source: string; confidence?: number; evidence: Evidence[] };
  cost: { statement: string; amount?: number; currency?: string; evidence: Evidence[] };
  attribution: { party: string | null; statement: string; evidence: Evidence[] };
  defensibility: { grade: Grade; reasons: string[]; regulatory: string[]; caveats: string[] };
}

export interface IntelligenceReport {
  generatedAt: string;
  siteCode: string;
  findings: Finding[];
  summary: {
    ahead: number;
    now: number;
    within30Days: number;
    attributable: number;
    exposure: { amount: number; currency: string; priced: number; unpriced: number };
    byGrade: Record<Grade, number>;
  };
  /** every record cited, once */
  appendix: Evidence[];
  /** inputs the platform could not supply, so the reader knows what is absent */
  gaps: string[];
}

export interface ReportInputs {
  now: Date;
  siteCode: string;
  currency?: string;
  alerts: Alert[];
  explanations: Record<string, AlertExplanation | undefined>;
  rules: Rule[];
  workOrders: WorkOrder[];
  testing: TestingSummary | null;
  compliance: ComplianceReport | null;
  liability: LiabilityRegister | null;
  populations: Population[];
  fits: Record<string, Fit[]>;
  drift: DriftCheck[];
  ledger: { fact: FactSummary; divergence: Divergence | null }[];
  holdPoints: HoldPointReport | null;
  gaps?: string[];
}

const DAY = 86_400_000;

export function money(amount: number, currency: string): string {
  try {
    /* a plain space, not the formatter's non-breaking one: this text is searched and read aloud */
    return new Intl.NumberFormat("en", { style: "currency", currency, maximumFractionDigits: 0 })
      .format(amount)
      .replace(/\u00a0/g, " ");
  } catch {
    return `${Math.round(amount).toLocaleString("en")} ${currency}`;
  }
}

function daysBetween(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / DAY);
}

function horizonFromDate(now: Date, at: string | undefined): Finding["horizon"] {
  if (!at) return { text: t("no date the platform can give") };
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return { text: t("no date the platform can give") };
  const days = daysBetween(now, d);
  const when = d.toISOString().slice(0, 10);
  if (days < 0) return { text: `${tn(-days, "{n} day ago", "{n} days ago")} (${when})`, at, days };
  if (days === 0) return { text: t("today ({when})", { when }), at, days };
  return { text: `${tn(days, "in {n} day", "in {n} days")} (${when})`, at, days };
}

/*
 * Caveats are written in the reader's language, so the grade cannot rest on
 * their English wording alone: a caveat that makes a finding weak is marked
 * as it is written, and the two that do not count against the grade are
 * compared as written.
 */
const WEAK = new Set<string>();
const weakCaveat = (c: string) => {
  WEAK.add(c);
  return c;
};
const noCost = () => t("no priced cost");
const noParty = () => t("no responsible party on record");
const countsAgainst = (c: string) => !/^no (priced|responsible)/.test(c) && c !== noCost() && c !== noParty();

function grade(caveats: string[], strongReasons: string[]): Grade {
  if (caveats.some((c) => WEAK.has(c) || /^(unverified|demonstration|disabled)/i.test(c))) return "weak";
  if (caveats.length > 0) return "partial";
  return strongReasons.length > 0 ? "strong" : "partial";
}

/* ── evidence builders ────────────────────────────────────────────────── */

const woEvidence = (w: WorkOrder): Evidence => ({
  kind: "work_order",
  ref: w.number,
  label: w.title,
  detail: `${w.status.replace("_", " ")} · P${w.priority}${w.crew ? ` · ${w.crew}` : ""}${w.dueAt ? ` · ${t("due {date}", { date: w.dueAt.slice(0, 10) })}` : ""}`,
  href: `/work-orders/${w.id}`,
});

/* the asset or zone is what a reader recognises; the alert's own id stays in the detail */
const alertEvidence = (a: Alert): Evidence => ({
  kind: "alert",
  ref: a.assetTag ?? a.zoneCode ?? a.id.slice(0, 8),
  label: a.title,
  detail: t("{kind} · severity {severity} · raised {date} · alert {id}", {
    kind: a.kind,
    severity: a.severity,
    date: a.raisedAt.slice(0, 10),
    id: a.id,
  }),
  href: `/alerts?open=${a.id}`,
});

const ruleEvidence = (key: string, rule: Rule | undefined, x: AlertExplanation | undefined): Evidence => ({
  kind: "rule",
  ref: key,
  label: rule?.name ?? x?.ruleName ?? key,
  detail: x?.expression ?? rule?.trigger,
  href: "/compliance",
});

const liabilityEvidence = (r: LiabilityRow): Evidence => ({
  kind: "liability",
  ref: r.id,
  label: r.title,
  detail: r.priced
    ? `${t("now {amount}", { amount: r.costNow != null ? money(r.costNow, r.currency ?? "SAR") : "—" })}${r.costDeferred != null ? t(", deferred {amount}", { amount: money(r.costDeferred, r.currency ?? "SAR") }) : ""}${r.basis?.rateSource ? ` · ${r.basis.rateSource}` : ""}`
    : t("not priced — {why}", { why: r.whyNot ?? t("no basis") }),
  href: "/liability",
});

/* ── the cost column ──────────────────────────────────────────────────── */

function costFor(
  liability: LiabilityRegister | null,
  currency: string,
  match: (r: LiabilityRow) => boolean,
  fallback: { amount?: number; label?: string; evidence?: Evidence[] } = {},
): Finding["cost"] {
  const rows = liability?.register.filter(match) ?? [];
  const priced = rows.filter((r) => r.priced && r.costNow != null);
  if (priced.length > 0) {
    const amount = priced.reduce((t, r) => t + (r.costNow ?? 0), 0);
    const deferred = priced.reduce((t, r) => t + (r.costDeferred ?? r.costNow ?? 0), 0);
    const cur = priced[0].currency ?? currency;
    return {
      statement:
        deferred > amount
          ? t("{now} to do now; {deferred} if deferred — from the deferred-cost register.", {
              now: money(amount, cur),
              deferred: money(deferred, cur),
            })
          : t("{amount} from the deferred-cost register.", { amount: money(amount, cur) }),
      amount,
      currency: cur,
      evidence: priced.map(liabilityEvidence),
    };
  }
  if (rows.length > 0) {
    return {
      statement: t("Not priced: {why}. The platform does not guess a figure.", {
        why: rows[0].whyNot ?? t("the register holds no rate for it"),
      }),
      evidence: rows.map(liabilityEvidence),
    };
  }
  if (fallback.amount != null) {
    return {
      statement: fallback.label
        ? `${money(fallback.amount, currency)} ${fallback.label}.`
        : t("{amount} planned on the work order.", { amount: money(fallback.amount, currency) }),
      amount: fallback.amount,
      currency,
      evidence: fallback.evidence ?? [],
    };
  }
  return {
    statement: t(
      "Not priced: nothing in the deferred-cost register or on a work order names this. The platform does not guess a figure.",
    ),
    evidence: [],
  };
}

/* ── the attribution column ───────────────────────────────────────────── */

function attributionFromWork(now: Date, orders: WorkOrder[]): Finding["attribution"] | null {
  if (orders.length === 0) return null;
  const open = orders.filter((w) => isOpen(w.status));
  const w = open[0] ?? orders[0];
  const overdue = w.dueAt && isOpen(w.status) ? daysBetween(new Date(w.dueAt), now) : 0;
  const late = overdue > 0 ? tn(overdue, ", {n} day past its due date", ", {n} days past its due date") : "";
  const status = w.status.replace("_", " ");
  if (!w.crew) {
    /* no crew named: the discipline the work sits with is still a party, and a
       weaker one — the report says a crew is missing rather than that nobody is */
    if (w.discipline) {
      return {
        party: t("{discipline} discipline", { discipline: w.discipline }),
        statement: t(
          "{number} sits with the {discipline} discipline{late}, with no crew named on it. Naming one is the operator's decision.",
          { number: w.number, discipline: w.discipline, late },
        ),
        evidence: orders.slice(0, 3).map(woEvidence),
      };
    }
    return {
      party: null,
      statement: t(
        "{number} is {status} with no crew or discipline on it, so there is nobody to hold to it yet. Assigning it is the operator's decision.",
        { number: w.number, status },
      ),
      evidence: orders.slice(0, 3).map(woEvidence),
    };
  }
  return {
    party: w.crew,
    statement:
      overdue > 0
        ? t("{crew} holds {number}{late} and still {status}.", { crew: w.crew, number: w.number, late, status })
        : isOpen(w.status)
          ? t("{crew} holds {number}, {status}{due}.", {
              crew: w.crew,
              number: w.number,
              status,
              due: w.dueAt ? t(", due {date}", { date: w.dueAt.slice(0, 10) }) : "",
            })
          : t("{crew} last worked this on {number} ({status}).", { crew: w.crew, number: w.number, status }),
    evidence: orders.slice(0, 3).map(woEvidence),
  };
}

/* ── finding builders ─────────────────────────────────────────────────── */

/**
 * One finding per thing, not per alert. A rule that fires once per lamp puts
 * twelve identical rows in front of the reader; the twelve are one finding —
 * "twelve lamps by the same date" — with all twelve alerts as its evidence.
 * The same for a reactive rule that fired on five defects at once.
 */
function groupAlerts(alerts: Alert[]): { lead: Alert; members: Alert[] }[] {
  const groups = new Map<string, { lead: Alert; members: Alert[] }>();
  for (const a of alerts) {
    /* the same rule, the same words, the same date: one finding. A reactive
       alert has no date, so its group is the rule and the words alone. */
    const key = `${a.kind}|${a.ruleKey ?? a.title}|${a.title}|${a.predictedFor?.slice(0, 10) ?? ""}`;
    const g = groups.get(key);
    if (g) g.members.push(a);
    else groups.set(key, { lead: a, members: [a] });
  }
  return [...groups.values()];
}

function alertFindings(inp: ReportInputs, currency: string): Finding[] {
  const rulesByKey = new Map(inp.rules.map((r) => [r.key, r]));
  const out: Finding[] = [];
  const live = inp.alerts.filter((a) => a.status === "open" || a.status === "acknowledged");
  for (const { lead: a, members } of groupAlerts(live)) {
    const x = inp.explanations[a.id];
    const rule = a.ruleKey ? rulesByKey.get(a.ruleKey) : undefined;
    const ahead = a.kind === "predictive";
    if (!ahead && a.severity < 4) continue; // already-broken items: only what would stop the airport
    const many = members.length > 1;
    const places = [...new Set(members.map((m) => m.assetTag ?? m.zoneCode).filter((v): v is string => !!v))];
    const ruleName = x?.ruleName ?? rule?.name ?? a.ruleKey ?? "unnamed";

    /* prediction */
    const resolved = x?.resolved
      ? Object.entries(x.resolved).filter(([, v]) => typeof v === "number" || typeof v === "string")
      : [];
    const values = resolved
      .slice(0, 3)
      .map(([k, v]) => `${k} = ${String(v)}`)
      .join("; ");
    const params = x?.parameters
      ? Object.entries(x.parameters)
          .slice(0, 3)
          .map(([k, v]) => `${k} ${String(v)}`)
          .join(", ")
      : "";
    const prediction: Finding["prediction"] = {
      statement: ahead
        ? t('{title}{many}. The rule "{rule}" expects it{by}{confidence}.', {
            title: a.title,
            many: many
              ? `${t(" — {n} of them", { n: members.length })}${places.length ? ` (${places.slice(0, 4).join(", ")}${places.length > 4 ? t(", and {n} more", { n: places.length - 4 }) : ""})` : ""}`
              : "",
            rule: ruleName,
            by: a.predictedFor ? t(" by {date}", { date: a.predictedFor.slice(0, 10) }) : "",
            confidence:
              a.confidence != null ? t(" with {pct}% confidence", { pct: Math.round(a.confidence * 100) }) : "",
          })
        : t('{title}. Already failing: the rule "{rule}" fired on the platform\'s own readings.', {
            title: a.title,
            rule: ruleName,
          }),
      /* kept in English: the decision engine reads the parameters and readings back out of it */
      source: [
        x?.expression ? `Rule: ${x.expression}` : null,
        params ? `Parameters: ${params}` : null,
        values ? `Readings: ${values}` : null,
      ]
        .filter(Boolean)
        .join(" · "),
      confidence: a.confidence,
      evidence: [...members.map(alertEvidence), ...(a.ruleKey ? [ruleEvidence(a.ruleKey, rule, x)] : [])],
    };

    /* work on it: named in the alert first, then anything on the same asset or zone */
    const named = (x?.resultingActions ?? [])
      .map((r) => inp.workOrders.find((w) => w.number === r.ref || w.id === r.ref))
      .filter((w): w is WorkOrder => !!w);
    const related = inp.workOrders.filter(
      (w) =>
        !named.includes(w) &&
        members.some((m) => (m.assetTag && w.assetTag === m.assetTag) || (m.zoneCode && w.zoneCode === m.zoneCode)),
    );
    const orders = [...named, ...related.filter((w) => isOpen(w.status)), ...related.filter((w) => !isOpen(w.status))];
    const attribution: Finding["attribution"] = attributionFromWork(inp.now, orders) ?? {
      party: null,
      statement: ahead
        ? t(
            "No work has been raised for this yet, so there is nobody to hold to it. Raising it is the operator's decision — the alert has a button for it.",
          )
        : t("The alert stands with no work order against it. Nobody is on it."),
      evidence: [],
    };

    /* cost: the register row for the same asset or zone, else what the work order plans */
    const planned = orders.find((w) => w.costPlanned != null);
    const cost = costFor(
      inp.liability,
      currency,
      (r) => members.some((m) => [m.assetTag, m.zoneCode].some((k) => k && (r.title.includes(k) || r.id === k))),
      planned ? { amount: planned.costPlanned, evidence: [woEvidence(planned)] } : {},
    );

    /* defensibility */
    const caveats: string[] = [];
    const reasons: string[] = [];
    const regulatory: string[] = [];
    if (x?.reference ?? rule?.reference) regulatory.push((x?.reference ?? rule?.reference) as string);
    if (rule?.verifiedAt || rule?.confirmedAgainst)
      reasons.push(
        t("threshold verified{against}{on}", {
          against: rule.confirmedAgainst ? t(" against {what}", { what: rule.confirmedAgainst }) : "",
          on: rule.verifiedAt ? t(" on {date}", { date: rule.verifiedAt.slice(0, 10) }) : "",
        }),
      );
    else if (rule?.verifyRequired || x?.verifyRequired)
      caveats.push(
        weakCaveat(t("unverified threshold: the rule's parameters have not been confirmed against a source document")),
      );
    if (rule && !rule.enabled)
      caveats.push(
        weakCaveat(t("disabled rule: {reason}", { reason: rule.gatedReason ?? t("the rule is switched off") })),
      );
    if (x?.resolved && Object.keys(x.resolved).length > 0)
      reasons.push(t("the readings that fired the rule are on record"));
    if (x?.confirmed === true) reasons.push(t("confirmed by a person"));
    if (x?.confirmed === false) caveats.push(t("dismissed by a person on review"));
    if (ahead) {
      const check = driftFor(inp.drift, a.ruleKey);
      if (check) {
        const bad = /drift|no longer|degrad|fail/i.test(check.verdict);
        if (check.conclusive && !bad)
          reasons.push(
            t("the model's last check ({date}) found it still describes the estate", {
              date: check.windowTo.slice(0, 10),
            }),
          );
        else if (bad) caveats.push(t('model drift: the last check found "{verdict}"', { verdict: check.verdict }));
        else caveats.push(t("model check inconclusive: {verdict}", { verdict: check.verdict }));
      } else caveats.push(t("no drift check is on record for the model behind this prediction"));
      if (a.confidence != null && a.confidence < 0.7)
        caveats.push(
          t("confidence {pct}% is below the level the platform treats as firm", {
            pct: Math.round(a.confidence * 100),
          }),
        );
    }
    if (attribution.party === null) caveats.push(noParty());
    else if (!orders.some((w) => w.crew)) caveats.push(t("no crew named, only the discipline"));
    if (cost.amount == null) caveats.push(noCost());

    out.push({
      id: `alert:${a.id}`,
      headline: many
        ? t("{title} — {items}", { title: a.title, items: tn(members.length, "{n} item", "{n} items") })
        : a.title,
      where: many
        ? places.length > 3
          ? `${places.slice(0, 3).join(", ")} +${places.length - 3}`
          : places.join(", ")
        : (a.zoneCode ?? a.assetTag),
      severity: Math.max(1, 6 - a.severity),
      phase: ahead ? "ahead" : "now",
      horizon: ahead
        ? horizonFromDate(inp.now, a.predictedFor)
        : {
            text: t("open since {date}", { date: a.raisedAt.slice(0, 10) }),
            at: a.raisedAt,
            days: daysBetween(inp.now, new Date(a.raisedAt)),
          },
      prediction,
      cost,
      attribution,
      defensibility: { grade: grade(caveats, reasons), reasons, regulatory, caveats },
    });
  }
  return out;
}

function driftFor(drift: DriftCheck[], ruleKey: string | undefined): DriftCheck | undefined {
  if (!ruleKey) return undefined;
  const tokens = ruleKey.split(/[_-]/).filter((t) => t.length > 3 && !/^(end|life|risk|below|above|of|the)$/.test(t));
  const hits = drift.filter((d) => tokens.some((t) => d.modelKey.includes(t)));
  return hits.sort((a, b) => (b.checkedAt ?? b.windowTo).localeCompare(a.checkedAt ?? a.windowTo))[0];
}

function slaFindings(inp: ReportInputs, currency: string): Finding[] {
  const out: Finding[] = [];
  for (const w of inp.workOrders) {
    if (!isOpen(w.status)) continue;
    const due = w.dueAt
      ? new Date(w.dueAt)
      : w.slaHours != null
        ? new Date(new Date(w.openedAt).getTime() + w.slaHours * 3_600_000)
        : null;
    if (!due || due.getTime() >= inp.now.getTime()) continue;
    const over = daysBetween(due, inp.now);
    const caveats: string[] = [];
    const reasons = [
      t("opened, due and current state are the platform's own dated records"),
      t("every transition is on the audit trail"),
    ];
    const attribution: Finding["attribution"] = attributionFromWork(inp.now, [w]) ?? {
      party: null,
      statement: t("{number} is past due with nobody on it.", { number: w.number }),
      evidence: [woEvidence(w)],
    };
    if (attribution.party === null) caveats.push(noParty());
    else if (!w.crew) caveats.push(t("no crew named, only the discipline"));
    const cost = costFor(
      inp.liability,
      currency,
      (r) => [w.assetTag, w.zoneCode].some((k) => k && r.title.includes(k)),
      w.costPlanned != null ? { amount: w.costPlanned, evidence: [woEvidence(w)] } : {},
    );
    if (cost.amount == null) caveats.push(noCost());
    const overDays = tn(over, "{n} day", "{n} days");
    out.push({
      id: `sla:${w.id}`,
      headline: t("{number} past its due date: {title}", { number: w.number, title: w.title }),
      where: w.zoneCode ?? w.assetTag,
      severity: w.priority,
      phase: "now",
      horizon: {
        text: t("{days} overdue (due {date})", { days: overDays, date: due.toISOString().slice(0, 10) }),
        at: due.toISOString(),
        days: -over,
      },
      prediction: {
        statement: t(
          "Priority {priority} work is {days} past the date it was due, and still {status}. Whatever it was raised to prevent is now unprotected.",
          { priority: w.priority, days: overDays, status: w.status.replace("_", " ") },
        ),
        source: `${t("Opened {date}", { date: w.openedAt.slice(0, 10) })}${w.slaHours != null ? t(", SLA {h} h", { h: w.slaHours }) : ""}${w.defectTypeName ? ` · ${w.defectTypeName}` : ""}`,
        evidence: [woEvidence(w)],
      },
      cost,
      attribution,
      defensibility: { grade: grade(caveats, reasons), reasons, regulatory: [], caveats },
    });
  }
  return out;
}

function testFindings(inp: ReportInputs): Finding[] {
  if (!inp.testing) return [];
  const out: Finding[] = [];
  for (const ty of inp.testing.types) {
    const failed = ty.last?.outcome === "fail";
    const overdue = ty.state === "overdue";
    const soon = ty.state === "due_soon";
    if (!failed && !overdue && !soon) continue;
    const caveats: string[] = [];
    const reasons: string[] = [];
    if (ty.last) reasons.push(t("the last record {ref} is on file with its result", { ref: ty.last.reference }));
    if (ty.intervalStatus === "proposed")
      caveats.push(t("the test interval is proposed, not agreed with the regulator"));
    if (ty.last?.demo) caveats.push(weakCaveat(t("demonstration record: not a real test")));
    if (ty.last?.judged === false && ty.mode === "judged") caveats.push(t("the result was never judged"));
    const evidence: Evidence[] = ty.last
      ? [
          {
            kind: "test",
            ref: ty.last.reference,
            label: `${ty.name} — ${ty.last.outcome}`,
            detail: t("performed {date}{by} · {basis}", {
              date: ty.last.performedOn.slice(0, 10),
              by: ty.last.performedBy ? t(" by {name}", { name: ty.last.performedBy }) : "",
              basis: ty.last.outcomeBasis,
            }),
            href: `/testing-records/${ty.last.id}`,
          },
        ]
      : [];
    const regulatory = ty.frameworks;
    caveats.push(noCost());
    caveats.push(noParty());
    out.push({
      id: `test:${ty.testType}`,
      headline: failed
        ? t("{name} failed its last test", { name: ty.name })
        : overdue
          ? t("{name} is overdue", { name: ty.name })
          : t("{name} falls due soon", { name: ty.name }),
      severity: failed ? 1 : overdue ? 2 : 3,
      phase: failed || overdue ? "now" : "ahead",
      horizon: ty.nextDue
        ? horizonFromDate(inp.now, ty.nextDue)
        : { text: ty.state === "never_tested" ? t("never tested") : t("no interval agreed") },
      prediction: {
        statement: failed
          ? t(
              'The last {name} came back "fail" ({basis}). Until it passes, the surface or system it covers is out of tolerance on the platform\'s own record.',
              { name: ty.name, basis: ty.last?.outcomeBasis ?? t("against its threshold") },
            )
          : overdue
            ? t(
                "The {name} is past its interval{interval}. The platform cannot say the surface is within limits, because nobody has measured it in time.",
                { name: ty.name, interval: ty.intervalDays ? t(" of {n} days", { n: ty.intervalDays }) : "" },
              )
            : t("The {name} falls due{when}{interval}.", {
                name: ty.name,
                when: ty.nextDue ? t(" on {date}", { date: ty.nextDue.slice(0, 10) }) : t(" shortly"),
                interval: ty.intervalDays ? t(" (interval {n} days)", { n: ty.intervalDays }) : "",
              }),
        source: t("Mandatory under {frameworks} · {records} on file", {
          frameworks: ty.frameworks.join(", ") || t("the operator's own program"),
          records: tn(ty.records, "{n} record", "{n} records"),
        }),
        evidence,
      },
      cost: {
        statement: t(
          "Not priced: a missed test carries a regulatory exposure, not a repair cost the register can hold.",
        ),
        evidence: [],
      },
      attribution: {
        party: null,
        statement: t("The obligation to test is the operator's own; the platform names no contractor for it."),
        evidence,
      },
      defensibility: {
        grade: grade(caveats.filter(countsAgainst), reasons),
        reasons,
        regulatory,
        caveats,
      },
    });
  }
  return out;
}

function obligationFindings(inp: ReportInputs): Finding[] {
  if (!inp.compliance) return [];
  const out: Finding[] = [];
  for (const o of inp.compliance.obligations) {
    const daysLeft = o.dueAt ? daysBetween(inp.now, new Date(o.dueAt)) : null;
    const overdue = o.status === "overdue" || (o.status === "pending" && daysLeft !== null && daysLeft < 0);
    const dueSoon = o.status === "pending" && daysLeft !== null && daysLeft >= 0 && daysLeft <= 30;
    if (!overdue && !dueSoon) continue;
    const caveats: string[] = [];
    const reasons: string[] = [];
    if (o.evidenceCount > 0)
      reasons.push(tn(o.evidenceCount, "{n} piece of evidence filed", "{n} pieces of evidence filed"));
    else caveats.push(t("no evidence filed against the obligation"));
    caveats.push(noCost(), noParty());
    const ev: Evidence = {
      kind: "obligation",
      ref: o.periodKey ?? o.title,
      label: `${o.framework}: ${o.title}`,
      detail: `${o.status}${o.period ? ` · ${o.period}` : ""}${o.dueAt ? ` · ${t("due {date}", { date: o.dueAt.slice(0, 10) })}` : ""}`,
      href: "/compliance",
    };
    out.push({
      id: `obligation:${o.framework}:${o.periodKey ?? o.title}`,
      headline: overdue
        ? t("Overdue obligation: {title}", { title: o.title })
        : t("Obligation due: {title}", { title: o.title }),
      severity: overdue ? 2 : 3,
      phase: overdue ? "now" : "ahead",
      horizon: horizonFromDate(inp.now, o.dueAt),
      prediction: {
        statement: overdue
          ? t('{framework} requires "{title}"{period}, and the platform holds it as overdue.', {
              framework: o.framework,
              title: o.title,
              period: o.period ? t(" for {period}", { period: o.period }) : "",
            })
          : t('{framework} requires "{title}"{period} by {date}.', {
              framework: o.framework,
              title: o.title,
              period: o.period ? t(" for {period}", { period: o.period }) : "",
              date: o.dueAt?.slice(0, 10) ?? "undefined",
            }),
        source: t("Compliance framework {framework}", { framework: o.framework }),
        evidence: [ev],
      },
      cost: {
        statement: t("Not priced: the exposure is regulatory, not a repair the register can hold."),
        evidence: [],
      },
      attribution: {
        party: null,
        statement: t("An obligation is the operator's own; the platform names no contractor for it."),
        evidence: [ev],
      },
      defensibility: {
        grade: grade(caveats.filter(countsAgainst), reasons),
        reasons,
        regulatory: [o.framework],
        caveats,
      },
    });
  }
  return out;
}

function wearOutFindings(inp: ReportInputs): Finding[] {
  const out: Finding[] = [];
  const seen = new Set<string>();
  for (const p of inp.populations) {
    if (seen.has(p.population)) continue;
    seen.add(p.population);
    const fit = (inp.fits[p.population] ?? []).find(
      (f) => f.wearOut?.known && f.wearOut.rejectsConstantRate && f.beta > 1,
    );
    if (!fit) continue;
    const caveats: string[] = [];
    const reasons = [
      t("fitted on {units} units, {failures} failures, {censored} still running ({method})", {
        units: fit.units,
        failures: fit.failures,
        censored: fit.censored,
        method: fit.method,
      }),
    ];
    if (fit.wearOut?.likelihoodRatio != null && fit.wearOut.critical95 != null)
      reasons.push(
        t("constant-rate model rejected at 95% (likelihood ratio {ratio} against {critical})", {
          ratio: fit.wearOut.likelihoodRatio.toFixed(1),
          critical: fit.wearOut.critical95.toFixed(1),
        }),
      );
    if (fit.betaLow != null && fit.betaHigh != null && fit.betaHigh - fit.betaLow > 1)
      caveats.push(
        t("the shape is loosely known (β {low}–{high})", {
          low: fit.betaLow.toFixed(1),
          high: fit.betaHigh.toFixed(1),
        }),
      );
    caveats.push(t("individual unit ages are not held, so the platform cannot say which unit fails first"));
    caveats.push(noCost(), noParty());
    const ev: Evidence = {
      kind: "fit",
      ref: fit.id,
      label: `${p.population}: β ${fit.beta.toFixed(2)}, η ${Math.round(fit.eta)} ${fit.timeUnit}`,
      detail: fit.shapeVerdict,
      href: "/reliability",
    };
    out.push({
      id: `wearout:${p.population}`,
      headline: t("{population} population is wearing out", { population: p.population }),
      severity: 3,
      phase: "ahead",
      horizon: {
        text: t("failures cluster toward {eta} {unit} of service", { eta: Math.round(fit.eta), unit: fit.timeUnit }),
      },
      prediction: {
        statement: t(
          "{units} units of {population} are wearing out (shape β {beta} > 1): older units fail more often. Typical life is {eta} {unit}. {note}",
          {
            units: p.units,
            population: p.population,
            beta: fit.beta.toFixed(2),
            eta: Math.round(fit.eta),
            unit: fit.timeUnit,
            note: fit.wearOut?.note ?? t("Replacement should be planned by age, not by failure."),
          },
        ),
        source: t("Survival fit {id}{fitted}", {
          id: fit.id,
          fitted: fit.fittedAt ? t(", fitted {date}", { date: fit.fittedAt.slice(0, 10) }) : "",
        }),
        evidence: [ev],
      },
      cost: {
        statement: t("Not priced: a population-level finding; the register prices named candidates."),
        evidence: [],
      },
      attribution: {
        party: null,
        statement: t(
          "Ageing is nobody's fault. The decision it asks for is a replacement program, and that is the operator's.",
        ),
        evidence: [ev],
      },
      defensibility: {
        grade: grade(caveats.filter(countsAgainst), reasons),
        reasons,
        regulatory: [],
        caveats,
      },
    });
  }
  return out;
}

function ledgerFindings(inp: ReportInputs): Finding[] {
  const out: Finding[] = [];
  for (const { fact, divergence: d } of inp.ledger) {
    if (!d || !d.comparable || !d.systematic) continue;
    const later = (d.bLaterPct ?? 0) >= 50 ? d.partyB : d.partyA;
    const earlier = later === d.partyB ? d.partyA : d.partyB;
    const minutes = d.medianMinutes ?? (d.medianSeconds != null ? d.medianSeconds / 60 : undefined);
    const ev: Evidence = {
      kind: "ledger",
      ref: fact.factKey,
      label: `${fact.factKey}: ${d.partyA} vs ${d.partyB}`,
      detail: `${t("{n} comparable pairs", { n: d.pairs })}${minutes != null ? t(" · median {m} min apart", { m: minutes.toFixed(0) }) : ""}`,
      href: "/ledger",
    };
    const reasons = [
      t("{n} pairs of both parties' own assertions", { n: d.pairs }),
      t("the difference is systematic, not noise"),
    ];
    const caveats = [noCost()];
    out.push({
      id: `ledger:${fact.factKey}`,
      headline: t("{later}'s record of \"{fact}\" runs systematically later than {earlier}'s", {
        later,
        fact: fact.factKey,
        earlier,
      }),
      severity: 3,
      phase: "now",
      horizon: {
        text: fact.latest ? t("latest assertion {date}", { date: fact.latest.slice(0, 10) }) : t("ongoing"),
      },
      prediction: {
        statement:
          d.finding ??
          t(
            '{later} records "{fact}" later than {earlier}{by}, across {pairs} comparable pairs. Whichever party\'s clock or process is wrong, every figure derived from this fact is wrong with it.',
            {
              later,
              fact: fact.factKey,
              earlier,
              by: minutes != null ? t(" by a median {m} minutes", { m: minutes.toFixed(0) }) : "",
              pairs: d.pairs,
            },
          ),
        source: t("Records ledger · {assertions} assertions by {parties} parties{authority}", {
          assertions: fact.assertions,
          parties: fact.parties,
          authority: fact.authority ? t(" · authority: {name}", { name: fact.authority }) : "",
        }),
        evidence: [ev],
      },
      cost: { statement: t("Not priced: a records dispute; its cost is in whatever the fact drives."), evidence: [] },
      attribution: {
        party: later,
        statement: t("{later} is the party whose assertions run late. {authority}", {
          later,
          authority: fact.authority
            ? t("The ledger names {name} as the authority on this fact.", { name: fact.authority })
            : t("The ledger names no authority on this fact, so which party is right is not settled here."),
        }),
        evidence: [ev],
      },
      defensibility: {
        grade: grade(
          caveats.filter((c) => !c.startsWith("no priced") && c !== noCost()),
          reasons,
        ),
        reasons,
        regulatory: [],
        caveats,
      },
    });
  }
  return out;
}

function holdPointFindings(inp: ReportInputs): Finding[] {
  if (!inp.holdPoints || inp.holdPoints.state !== "work_stopped") return [];
  const out: Finding[] = [];
  for (const h of inp.holdPoints.holdPoints) {
    const failed = h.result === "fail" || h.failed > 0;
    const ev: Evidence = {
      kind: "hold_point",
      ref: h.reference,
      label: `${h.projectCode}: ${h.title}`,
      detail: `${h.discipline}${h.location ? ` · ${h.location}` : ""}${h.performedAt ? ` · ${t("inspected {date}", { date: h.performedAt.slice(0, 10) })}` : h.scheduledFor ? ` · ${t("scheduled {date}", { date: h.scheduledFor.slice(0, 10) })}` : ""}${h.failed ? ` · ${tn(h.failed, "{n} failed item", "{n} failed items")}` : ""}`,
      href: `/capital-projects/inspections/${h.id}`,
    };
    const reasons = [t("the hold point, its items and their results are on the project record")];
    const caveats = [noCost(), t("the platform names the project, not the contractor behind it")];
    out.push({
      id: `hold:${h.id}`,
      headline: t("Work stopped on {code}{name} at {ref}", {
        code: h.projectCode,
        name: h.projectName ? ` (${h.projectName})` : "",
        ref: h.reference,
      }),
      where: h.location,
      severity: failed ? 2 : 3,
      phase: "now",
      horizon: {
        text: h.performedAt
          ? t("stopped since {date}", { date: h.performedAt.slice(0, 10) })
          : t("awaiting inspection"),
      },
      prediction: {
        statement: failed
          ? t(
              "The {discipline} hold point {ref} failed ({failed} of {items} items). The project cannot proceed past it, and every date after it moves.",
              { discipline: h.discipline, ref: h.reference, failed: h.failed, items: h.items },
            )
          : t(
              "The {discipline} hold point {ref} has not been released ({unchecked} of {items} items unchecked). The project is stopped at it.",
              { discipline: h.discipline, ref: h.reference, unchecked: h.notChecked, items: h.items },
            ),
        source: `${t("Project {code}", { code: h.projectCode })}${h.projectStatus ? ` · ${h.projectStatus}` : ""}`,
        evidence: [ev],
      },
      cost: { statement: t("Not priced: the register does not hold project delay costs."), evidence: [] },
      attribution: {
        party: h.projectCode,
        statement: t(
          "The project {code} is stopped at its own hold point. Which contractor answers for it is in the contract, not on the platform.",
          { code: h.projectCode },
        ),
        evidence: [ev],
      },
      defensibility: {
        grade: grade(
          caveats.filter((c) => !c.startsWith("no priced") && c !== noCost()),
          reasons,
        ),
        reasons,
        regulatory: [],
        caveats,
      },
    });
  }
  return out;
}

/* ── the report ───────────────────────────────────────────────────────── */

export function buildIntelligenceReport(inp: ReportInputs): IntelligenceReport {
  const currency = inp.currency ?? inp.liability?.register.find((r) => r.currency)?.currency ?? "SAR";
  const findings = [
    ...alertFindings(inp, currency),
    ...slaFindings(inp, currency),
    ...testFindings(inp),
    ...obligationFindings(inp),
    ...wearOutFindings(inp),
    ...ledgerFindings(inp),
    ...holdPointFindings(inp),
  ];
  /* what breaks soonest first, then the worst */
  findings.sort((a, b) => {
    if (a.phase !== b.phase) return a.phase === "ahead" ? -1 : 1;
    const da = a.horizon.days ?? 9999;
    const db = b.horizon.days ?? 9999;
    if (a.phase === "ahead" && da !== db) return da - db;
    return a.severity - b.severity;
  });

  const priced = findings.filter((f) => f.cost.amount != null);
  const exposure = priced.reduce((t, f) => t + (f.cost.amount ?? 0), 0);
  const byGrade: Record<Grade, number> = { strong: 0, partial: 0, weak: 0 };
  for (const f of findings) byGrade[f.defensibility.grade] += 1;

  const seen = new Set<string>();
  const appendix: Evidence[] = [];
  for (const f of findings) {
    for (const e of [...f.prediction.evidence, ...f.cost.evidence, ...f.attribution.evidence]) {
      const k = `${e.kind}:${e.ref}`;
      if (seen.has(k)) continue;
      seen.add(k);
      appendix.push(e);
    }
  }

  return {
    generatedAt: inp.now.toISOString(),
    siteCode: inp.siteCode,
    findings,
    summary: {
      ahead: findings.filter((f) => f.phase === "ahead").length,
      now: findings.filter((f) => f.phase === "now").length,
      within30Days: findings.filter((f) => f.phase === "ahead" && f.horizon.days != null && f.horizon.days <= 30)
        .length,
      attributable: findings.filter((f) => f.attribution.party !== null).length,
      exposure: {
        amount: exposure,
        currency: priced[0]?.cost.currency ?? currency,
        priced: priced.length,
        unpriced: findings.length - priced.length,
      },
      byGrade,
    },
    appendix,
    gaps: inp.gaps ?? [],
  };
}
